import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import dompurify from "dompurify";

import { axiosPrivate } from "../../api/axios";
import projStyles from "./ProjectRegistration.module.css";
import styles from "./CampRegistration.module.css";
import { formatMoney } from "../../utils/helpers";


const LEADER_TYPES = [
    "Leader"
];

const CHAPERONE_TYPES = [
    "Chaperone"
];

const GROUP_EVENT_TYPES = [
    "Camp",
    "Program",
    "General"
];


const normalize = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase();


function EventRegistration({
    Registrations: registrations = [],
    Event: bomEvent
}) {

    const navigate = useNavigate();


    // =========================================================
    // ENABLED REGISTRATIONS
    // =========================================================

    const enabledRegistrations = useMemo(() => {

        if (Array.isArray(registrations)) {

            return registrations.filter(
                registration =>
                    registration.RegistrationEnabled
            );

        }

        if (registrations) {
            return [registrations];
        }

        return [];

    }, [registrations]);


    // =========================================================
    // EVENT TYPE
    // =========================================================

    const eventType = bomEvent?.BP_Type || "";

    const normalizedEventType =
        normalize(eventType);

    const isProject =
        normalizedEventType === "project";

    const isDisasterRelief =
        normalizedEventType === "disaster relief";


    // =========================================================
    // PROJECT BEHAVIOR
    //
    // Projects currently use the first enabled registration.
    // Camp / other group events may have multiple registrations.
    // =========================================================

    const displayedRegistrations = useMemo(() => {

        if (isProject) {

            return enabledRegistrations.slice(0, 1);

        }

        return enabledRegistrations;

    }, [
        enabledRegistrations,
        isProject
    ]);


    // =========================================================
    // STATE
    // =========================================================

    const [
        selectedQuantities,
        setSelectedQuantities
    ] = useState(() =>
        Object.fromEntries(
            enabledRegistrations.map(
                registration => [
                    registration.RegistrationID,
                    0
                ]
            )
        )
    );


    /*
     * {
     *   [RegistrationID]: true / false
     * }
     */
    const [
        capacityStatus,
        setCapacityStatus
    ] = useState({});


    /*
     * {
     *   [RegistrationID]: number
     * }
     *
     * If unlimited, the registration is NOT placed
     * in this map.
     */
    const [
        capacityRemaining,
        setCapacityRemaining
    ] = useState({});


    /*
     * {
     *   [RegistrationID]: true / false
     * }
     */
    const [
        capacityUnlimited,
        setCapacityUnlimited
    ] = useState({});


    const [
        checkingCapacity,
        setCheckingCapacity
    ] = useState(true);


    /*
     * {
     *   [RegistrationID]: { value, label }
     * }
     */
    const [
        insuranceOptions,
        setInsuranceOptions
    ] = useState({});


    /*
     * {
     *   [RegistrationID]: string
     * }
     */
    const [
        insuranceSelections,
        setInsuranceSelections
    ] = useState({});


    const [
        agreementTemplate,
        setAgreementTemplate
    ] = useState({});


    /*
     * {
     *   [RegistrationID]: boolean
     * }
     */
    const [
        waiverAgreements,
        setWaiverAgreements
    ] = useState({});


    // =========================================================
    // KEEP QUANTITY STATE IN SYNC
    // =========================================================

    useEffect(() => {

        setSelectedQuantities(prev => {

            const next = {};

            enabledRegistrations.forEach(
                registration => {

                    next[
                        registration.RegistrationID
                    ] =
                        prev[
                            registration.RegistrationID
                        ] ?? 0;

                }
            );

            return next;

        });

    }, [enabledRegistrations]);


    // =========================================================
    // CAPACITY
    // =========================================================

    useEffect(() => {

        if (!enabledRegistrations.length) {

            setCheckingCapacity(false);

            return;

        }


        let cancelled = false;


        const checkCapacities = async () => {

            setCheckingCapacity(true);


            try {

                const results =
                    await Promise.all(

                        enabledRegistrations.map(
                            async registration => {

                                try {

                                    const response =
                                        await axiosPrivate.get(
                                            `/api/registrations/capacity/${registration.RegistrationID}`
                                        );


                                    const data =
                                        response.data;


                                    /*
                                     * New API response:
                                     *
                                     * {
                                     *   available: true,
                                     *   remaining: 5,
                                     *   unlimited: false,
                                     *   capacity: 20,
                                     *   attendeeCount: 15
                                     * }
                                     */


                                    if (
                                        data &&
                                        typeof data === "object"
                                    ) {

                                        const remaining =
                                            data.unlimited
                                                ? null
                                                : Number(
                                                    data.remaining ?? 0
                                                );


                                        return {

                                            id:
                                                registration.RegistrationID,

                                            available:
                                                data.available === true,

                                            remaining,

                                            unlimited:
                                                data.unlimited === true

                                        };

                                    }


                                    /*
                                     * Backward-compatible fallback
                                     * in case an endpoint still returns
                                     * true / false.
                                     */

                                    return {

                                        id:
                                            registration.RegistrationID,

                                        available:
                                            data === true,

                                        remaining:
                                            null,

                                        unlimited:
                                            false

                                    };

                                } catch (error) {

                                    console.error(
                                        `Unable to check capacity for registration ${registration.RegistrationID}`,
                                        error
                                    );


                                    /*
                                     * Fail closed.
                                     */

                                    return {

                                        id:
                                            registration.RegistrationID,

                                        available:
                                            false,

                                        remaining:
                                            0,

                                        unlimited:
                                            false

                                    };

                                }

                            }
                        )

                    );


                if (cancelled) {
                    return;
                }


                const statusMap = {};
                const remainingMap = {};
                const unlimitedMap = {};


                results.forEach(result => {

                    statusMap[result.id] =
                        result.available;


                    unlimitedMap[result.id] =
                        result.unlimited;


                    if (
                        result.remaining !== null &&
                        result.remaining !== undefined
                    ) {

                        remainingMap[result.id] =
                            Math.max(
                                0,
                                result.remaining
                            );

                    }

                });


                setCapacityStatus(
                    statusMap
                );

                setCapacityRemaining(
                    remainingMap
                );

                setCapacityUnlimited(
                    unlimitedMap
                );


                /*
                 * Automatically select one person for
                 * Leader and Chaperone registrations
                 * if they have capacity.
                 */

                setSelectedQuantities(prev => {

                    const next = {
                        ...prev
                    };


                    enabledRegistrations.forEach(
                        registration => {

                            const isSinglePerson =
                                LEADER_TYPES.includes(
                                    registration.SectionLabel
                                ) ||
                                CHAPERONE_TYPES.includes(
                                    registration.SectionLabel
                                );


                            if (
                                isSinglePerson &&
                                statusMap[
                                    registration.RegistrationID
                                ] &&
                                !next[
                                    registration.RegistrationID
                                ]
                            ) {

                                next[
                                    registration.RegistrationID
                                ] = 1;

                            }

                        }
                    );


                    return next;

                });

            } finally {

                if (!cancelled) {

                    setCheckingCapacity(
                        false
                    );

                }

            }

        };


        checkCapacities();


        return () => {

            cancelled = true;

        };

    }, [enabledRegistrations]);


    // =========================================================
    // INSURANCE
    // =========================================================

    useEffect(() => {

        const needsInsurance =
            enabledRegistrations.filter(
                registration =>
                    registration.RequireSupplementalInsurance &&
                    registration.SupplementalInsuranceType
            );


        if (!needsInsurance.length) {
            return;
        }


        let cancelled = false;


        const fetchInsuranceOptions =
            async () => {

                const results =
                    await Promise.all(

                        needsInsurance.map(
                            async registration => {

                                try {

                                    const response =
                                        await axiosPrivate.get(
                                            `/api/insurance/${registration.SupplementalInsuranceType}`
                                        );


                                    const option =
                                        response.data;


                                    return {

                                        id:
                                            registration.RegistrationID,

                                        option: {

                                            value:
                                                String(option.id),

                                            label:
                                                `${option.label} (${option.price})`

                                        }

                                    };

                                } catch (error) {

                                    console.error(
                                        `Error fetching insurance option for registration ${registration.RegistrationID}`,
                                        error
                                    );


                                    return {

                                        id:
                                            registration.RegistrationID,

                                        option:
                                            null

                                    };

                                }

                            }
                        )

                    );


                if (cancelled) {
                    return;
                }


                const optionsMap = {};
                const selectionsMap = {};


                results.forEach(
                    ({ id, option }) => {

                        if (!option) {
                            return;
                        }


                        optionsMap[id] =
                            option;


                        selectionsMap[id] =
                            option.value;

                    }
                );


                setInsuranceOptions(
                    prev => ({
                        ...prev,
                        ...optionsMap
                    })
                );


                setInsuranceSelections(
                    prev => ({
                        ...prev,
                        ...selectionsMap
                    })
                );

            };


        fetchInsuranceOptions();


        return () => {

            cancelled = true;

        };

    }, [enabledRegistrations]);


    // =========================================================
    // AGREEMENT
    // =========================================================

    useEffect(() => {

        const fetchAgreementTemplate =
            async () => {

                try {

                    const response =
                        await axiosPrivate.get(
                            "/api/agreements/1"
                        );


                    setAgreementTemplate(
                        response.data
                    );

                } catch (error) {

                    console.error(
                        "Unable to load agreement template",
                        error
                    );

                }

            };


        fetchAgreementTemplate();

    }, []);


    // =========================================================
    // EVENT / REGISTRATION HELPERS
    // =========================================================

    const isTeamRegistration =
        registration => {

            return (
                isProject &&
                registration?.AllowTeams === true
            );

        };


    const isGroupRegistration =
        registration => {

            /*
             * Projects use teams.
             *
             * Disaster Relief does not use either
             * the team or group quantity logic.
             *
             * Camp / Program / General use groups.
             */

            return (
                !isProject &&
                !isDisasterRelief &&
                GROUP_EVENT_TYPES.includes(
                    eventType
                ) &&
                registration?.AllowGroups === true
            );

        };


    const isTeamLeader =
        registration => {

            return (
                isTeamRegistration(
                    registration
                ) &&
                registration?.SectionLabel === "Leader"
            );

        };


    const isSinglePersonRegistration =
        registration => {

            return (
                LEADER_TYPES.includes(
                    registration?.SectionLabel
                ) ||
                CHAPERONE_TYPES.includes(
                    registration?.SectionLabel
                )
            );

        };


    // =========================================================
    // REGISTRATION QUANTITY LIMIT
    // =========================================================

    const getRegistrationQuantityLimit =
        registration => {

            if (!registration) {
                return 1;
            }


            /*
             * -----------------------------------------
             * PROJECT / TEAM
             * -----------------------------------------
             */

            if (
                isTeamRegistration(
                    registration
                )
            ) {

                /*
                 * Team Leader can ONLY ever register
                 * one person.
                 */

                if (
                    isTeamLeader(
                        registration
                    )
                ) {

                    return 1;

                }


                const maxTeamSize =
                    Number(
                        registration.MaxTeamSize
                    );


                return (
                    maxTeamSize > 0
                        ? maxTeamSize
                        : 1
                );

            }


            /*
             * -----------------------------------------
             * GROUP
             * -----------------------------------------
             */

            if (
                isGroupRegistration(
                    registration
                )
            ) {

                /*
                 * Group-specific maximum.
                 */

                const maxGroupSize =
                    Number(
                        registration.MaxGroupSize
                    );


                return (
                    maxGroupSize > 0
                        ? maxGroupSize
                        : 1
                );

            }


            /*
             * -----------------------------------------
             * NORMAL INDIVIDUAL
             * -----------------------------------------
             */

            return 1;

        };


    // =========================================================
    // FINAL QUANTITY LIMIT
    // =========================================================

    const getQuantityLimit =
        registration => {

            const registrationLimit =
                getRegistrationQuantityLimit(
                    registration
                );


            /*
             * Unlimited registration capacity.
             *
             * Only use the registration-specific
             * limit.
             */

            if (
                capacityUnlimited[
                    registration.RegistrationID
                ]
            ) {

                return registrationLimit;

            }


            /*
             * We have actual remaining capacity.
             */

            const remaining =
                capacityRemaining[
                    registration.RegistrationID
                ];


            if (
                Number.isFinite(remaining)
            ) {

                return Math.max(
                    0,
                    Math.min(
                        registrationLimit,
                        remaining
                    )
                );

            }


            /*
             * Backward compatibility / fallback:
             * RegistrationCapacity is still better
             * than no limit if the endpoint returned
             * only true/false.
             */

            const configuredCapacity =
                Number(
                    registration.RegistrationCapacity
                );


            if (
                configuredCapacity > 0
            ) {

                return Math.min(
                    registrationLimit,
                    configuredCapacity
                );

            }


            return registrationLimit;

        };


    // =========================================================
    // QUANTITY OPTIONS
    // =========================================================

    const buildQuantityOptions =
        registration => {

            const max =
                getQuantityLimit(
                    registration
                );


            return Array.from(
                {
                    length: max + 1
                },
                (_, index) => (

                    <option
                        key={index}
                        value={index}
                    >
                        {index}
                    </option>

                )
            );

        };


    // =========================================================
    // HANDLERS
    // =========================================================

    const handleQuantityChange =
        (registrationID, value) => {

            const quantity =
                parseInt(
                    value,
                    10
                );


            setSelectedQuantities(
                prev => ({
                    ...prev,

                    [registrationID]:
                        Number.isNaN(quantity)
                            ? 0
                            : quantity

                })
            );

        };


    const handleInsuranceChange =
        (registrationID, value) => {

            setInsuranceSelections(
                prev => ({
                    ...prev,

                    [registrationID]:
                        value
                })
            );

        };


    const handleWaiverChange =
        (registrationID, value) => {

            setWaiverAgreements(
                prev => ({
                    ...prev,

                    [registrationID]:
                        value === "1"
                })
            );

        };


    // =========================================================
    // REGISTRATION READY
    // =========================================================

    const isRegistrationReady =
        registration => {

            const quantity =
                selectedQuantities[
                    registration.RegistrationID
                ] || 0;


            const capacityOkay =
                capacityStatus[
                    registration.RegistrationID
                ] === true;


            const waiverOkay =
                !registration.WaiverProject ||
                waiverAgreements[
                    registration.RegistrationID
                ] === true;


            return (
                quantity > 0 &&
                capacityOkay &&
                waiverOkay
            );

        };


    // =========================================================
    // REGISTER
    // =========================================================

    const handleRegister = () => {

        const selected =
            enabledRegistrations

                .filter(
                    isRegistrationReady
                )

                .map(registration => ({

                    ...registration,

                    SelectedInsurance:
                        registration.RequireSupplementalInsurance
                            ? insuranceSelections[
                                registration.RegistrationID
                            ] ?? null
                            : null,

                    WaiverAgreed:
                        registration.WaiverProject
                            ? Boolean(
                                waiverAgreements[
                                    registration.RegistrationID
                                ]
                            )
                            : null,

                    Quantity:
                        selectedQuantities[
                            registration.RegistrationID
                        ]

                }));


        if (!selected.length) {
            return;
        }


        /*
         * Preserve existing behavior:
         * first selected insurance option wins.
         */

        const insuranceId =
            selected.find(
                registration =>
                    registration.SelectedInsurance &&
                    registration.SelectedInsurance !== "0"
            )?.SelectedInsurance ?? null;


        navigate(
            `/events/register/${bomEvent.EventsID}`,
            {
                state: {

                    Event:
                        bomEvent,

                    Registrations:
                        selected,

                    Insurance:
                        insuranceId

                }
            }
        );

    };


    // =========================================================
    // GLOBAL SELECTION STATUS
    // =========================================================

    // A registration the user has actually opted into (quantity > 0).
const isSelected =
    registration =>
        (selectedQuantities[registration.RegistrationID] || 0) > 0;

const selectedRegistrations =
    enabledRegistrations.filter(isSelected);

const anySelected =
    selectedRegistrations.length > 0;

// Every registration that's been selected must ALSO satisfy capacity
// and waiver requirements — one incomplete selection blocks Continue
// for the whole page, regardless of how many other registrations exist.
const allSelectedReady =
    selectedRegistrations.every(isRegistrationReady);


    // =========================================================
    // DISPLAY HELPERS
    // =========================================================

    const fmtMoney =
        value => {

            if (
                value === undefined ||
                value === null ||
                value === ""
            ) {

                return "";

            }


            const num =
                typeof value === "number"
                    ? value
                    : parseFloat(
                        String(value)
                            .replace(
                                /[^0-9.]/g,
                                ""
                            )
                    );


            if (
                Number.isNaN(num)
            ) {

                return String(value);

            }


            return formatMoney(num);

        };


    const priceLine =
        registration => {

            if (!registration) {
                return "";
            }


            const base =
                fmtMoney(
                    registration.RegistrationCost
                );


            if (
                registration.RegistrationRequiredCost &&
                registration.RegistrationRequiredPrice != null
            ) {

                return (
                    `${base} + ` +
                    `${fmtMoney(
                        registration.RegistrationRequiredPrice
                    )} (Additional Cost)`
                );

            }


            return base;

        };


    const depositLine =
        registration => {

            if (!registration) {
                return "";
            }


            const hasDeposit =
                registration.RegistrationAllowDeposit &&
                registration.RegistrationDepositAmount != null;


            if (
                hasDeposit &&
                parseInt(
                    registration.RegistrationDepositDueDays
                ) >= 0
            ) {

                return (
                    `Deposit Amount - ` +
                    `${fmtMoney(
                        registration.RegistrationDepositAmount
                    )}. ` +
                    `Balance due ` +
                    `${registration.RegistrationDepositDueDays} ` +
                    `days before. You will be able to choose ` +
                    `this option during checkout.`
                );

            }


            if (hasDeposit) {

                return (
                    `Deposit Amount - ` +
                    `${fmtMoney(
                        registration.RegistrationDepositAmount
                    )}. ` +
                    `You will be able to choose this option ` +
                    `during checkout.`
                );

            }


            return "";

        };


    const bgCheckLine =
        registration => {

            if (
                !registration?.RequireBGCheck
            ) {

                return null;

            }


            const typeMap = {

                0:
                    "BOM Conducted",

                1:
                    "Self-Conducted"

            };


            const checkType =
                typeMap[
                    registration.BGCheckType
                ] ?? "Standard";


            return (
                `A background check is required for this ` +
                `registration (${checkType}).`
            );

        };


    // =========================================================
    // EMPTY
    // =========================================================

    if (
        displayedRegistrations.length === 0
    ) {

        return null;

    }


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <>

            <div
                className={
                    styles.registrationPanel
                }
            >

                {displayedRegistrations.map(
                    (registration, index) => {

                        const registrationID =
                            registration.RegistrationID;


                        const quantity =
                            selectedQuantities[
                                registrationID
                            ] || 0;


                        const quantityLimit =
                            getQuantityLimit(
                                registration
                            );


                        const registrationHasCapacity =
                            capacityStatus[
                                registrationID
                            ] === true;


                        const registrationIsUnlimited =
                            capacityUnlimited[
                                registrationID
                            ] === true;


                        const isTeam =
                            isTeamRegistration(
                                registration
                            );


                        const isGroup =
                            isGroupRegistration(
                                registration
                            );


                        const isLeader =
                            isTeamLeader(
                                registration
                            );


                        const insuranceOption =
                            insuranceOptions[
                                registrationID
                            ];


                        const insuranceSelection =
                            insuranceSelections[
                                registrationID
                            ] ?? "";


                        const waiverAgreed =
                            waiverAgreements[
                                registrationID
                            ] ?? false;


                        const quantitySelected =
                            quantity > 0;


                        const waiverMissing =
                            registration.WaiverProject &&
                            quantitySelected &&
                            !waiverAgreed;


                        const remaining =
                            capacityRemaining[
                                registrationID
                            ];


                        return (

                            <div
                                key={
                                    registrationID
                                }

                                className={`
                                    ${styles.campOption}
                                    ${
                                        index > 0
                                            ? styles.campOptionDivider
                                            : ""
                                    }
                                `}
                            >

                                <div
                                    className={
                                        styles.campOptionGrid
                                    }
                                >

                                    {/* ================================= */}
                                    {/* REGISTRATION LABEL */}
                                    {/* ================================= */}

                                    <div
                                        className={
                                            styles.campLabelCell
                                        }
                                    >

                                        {index === 0 && (

                                            <div
                                                className={
                                                    styles.campRegLabel
                                                }
                                            >
                                                Registration
                                            </div>

                                        )}

                                    </div>


                                    {/* ================================= */}
                                    {/* CONTENT */}
                                    {/* ================================= */}

                                    <div
                                        className={
                                            styles.campContentCol
                                        }
                                    >

                                        <h2
                                            className={
                                                styles.campOptionTitle
                                            }
                                        >
                                            {
                                                registration.RegistrationName
                                            }
                                        </h2>


                                        {/* Description */}

                                        {registration.RegistrationDescription && (

                                            <div
                                                className={
                                                    projStyles.regDescription
                                                }

                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        dompurify.sanitize(
                                                            registration.RegistrationDescription
                                                        )
                                                }}
                                            />

                                        )}


                                        {/* Price */}

                                        <p
                                            className={
                                                projStyles.regPrice
                                            }
                                        >
                                            {
                                                priceLine(
                                                    registration
                                                )
                                            }
                                        </p>


                                        {/* Deposit */}

                                        {depositLine(
                                            registration
                                        ) && (

                                            <p
                                                className={
                                                    projStyles.regDescription
                                                }
                                            >
                                                {
                                                    depositLine(
                                                        registration
                                                    )
                                                }
                                            </p>

                                        )}


                                        {/* Background Check */}

                                        {bgCheckLine(
                                            registration
                                        ) && (

                                            <div
                                                className={
                                                    projStyles.bgCheckWarning
                                                }
                                            >

                                                <span
                                                    className={
                                                        projStyles.bgCheckIcon
                                                    }
                                                >
                                                    ⚠️
                                                </span>

                                                <span>
                                                    {
                                                        bgCheckLine(
                                                            registration
                                                        )
                                                    }
                                                </span>

                                            </div>

                                        )}


                                        {/* ================================= */}
                                        {/* TEAM / GROUP INFORMATION */}
                                        {/* ================================= */}

                                        {isTeam && (

                                            <p
                                                className={
                                                    projStyles.regDescription
                                                }
                                            >

                                                {isLeader
                                                    ? "Team Leader registration — 1 person"
                                                    : quantityLimit > 1
                                                        ? `Team registration — up to ${quantityLimit} people`
                                                        : "Team registration"
                                                }

                                            </p>

                                        )}


                                        {isGroup && (

                                            <p
                                                className={
                                                    projStyles.regDescription
                                                }
                                            >

                                                {quantityLimit > 1
                                                    ? `Group registration — up to ${quantityLimit} people`
                                                    : ""
                                                }

                                            </p>

                                        )}


                                        {/* ================================= */}
                                        {/* CAPACITY MESSAGE */}
                                        {/* ================================= */}

                                        {!checkingCapacity &&
                                            !registrationHasCapacity && (

                                                <div
                                                    className={
                                                        styles.capacityError
                                                    }
                                                >

                                                    This registration has
                                                    reached maximum capacity
                                                    and is no longer accepting
                                                    registrations.

                                                </div>

                                            )}


                                        {/* ================================= */}
                                        {/* REMAINING CAPACITY */}
                                        {/* ================================= */}

                                        {!checkingCapacity &&
                                            registrationHasCapacity &&
                                            !registrationIsUnlimited &&
                                            Number.isFinite(
                                                remaining
                                            ) &&
                                            remaining > 0 && (

                                                <small
                                                    className={
                                                        projStyles.regDescription
                                                    }
                                                >

                                                    {
                                                        remaining === 1
                                                            ? "1 spot remaining"
                                                            : `${remaining} spots remaining`
                                                    }

                                                </small>

                                            )}


                                        {/* ================================= */}
                                        {/* QUANTITY */}
                                        {/* ================================= */}

                                        <div
                                            className={
                                                styles.campQtyCell
                                            }
                                        >

                                            <label
                                                htmlFor={
                                                    `qty-${registrationID}`
                                                }
                                            >
                                                Quantity
                                            </label>


                                            <select

                                                id={
                                                    `qty-${registrationID}`
                                                }

                                                name={
                                                    `qty-${registrationID}`
                                                }

                                                disabled={
                                                    !registrationHasCapacity ||
                                                    quantityLimit <= 0
                                                }

                                                className={`
                                                    DropDownField
                                                    form-control
                                                    ${styles.campQtySelect}
                                                `}

                                                value={
                                                    quantity
                                                }

                                                onChange={e =>
                                                    handleQuantityChange(
                                                        registrationID,
                                                        e.target.value
                                                    )
                                                }

                                            >

                                                {
                                                    buildQuantityOptions(
                                                        registration
                                                    )
                                                }

                                            </select>


                                        </div>


                                        {/* ================================= */}
                                        {/* INSURANCE */}
                                        {/* ================================= */}

                                        {
                                            registration.RequireSupplementalInsurance &&
                                            insuranceOption
                                                ? (

                                                    <div
                                                        className={
                                                            styles.regRow
                                                        }
                                                    >

                                                        <div>

                                                            <p
                                                                className={
                                                                    styles.regSectionLabel
                                                                }
                                                            >
                                                                Supplemental Insurance
                                                            </p>


                                                            <select

                                                                name={
                                                                    `insurance-${registrationID}`
                                                                }

                                                                id={
                                                                    `insurance-${registrationID}`
                                                                }

                                                                className={`
                                                                    DropDownField
                                                                    form-control
                                                                    ${styles.insuranceSelect}
                                                                `}

                                                                onChange={e =>
                                                                    handleInsuranceChange(
                                                                        registrationID,
                                                                        e.target.value
                                                                    )
                                                                }

                                                                value={
                                                                    insuranceSelection
                                                                }

                                                            >

                                                                <option
                                                                    value={
                                                                        insuranceOption.value
                                                                    }
                                                                >
                                                                    {
                                                                        insuranceOption.label
                                                                    }
                                                                </option>

                                                                <option value="0">
                                                                    I will provide
                                                                    my own insurance
                                                                </option>

                                                            </select>


                                                            <p
                                                                className={
                                                                    styles.insuranceHint
                                                                }
                                                            >
                                                                Helps cover medical
                                                                gaps and emergencies
                                                            </p>

                                                        </div>

                                                    </div>

                                                )
                                                : null
                                        }


                                        {/* ================================= */}
                                        {/* WAIVER */}
                                        {/* ================================= */}

                                        {
                                            registration.WaiverProject && (

                                                <div
                                                    className={`
                                                        ${styles.regRow}
                                                        ${styles.regRowMatchSelect}
                                                    `}
                                                >

                                                    <div
                                                        className={
                                                            styles.regSectionLabel
                                                        }
                                                    >
                                                        Waiver
                                                    </div>


                                                    <div>

                                                        <select

                                                            id={
                                                                `waiver-${registrationID}`
                                                            }

                                                            name={
                                                                `waiver-${registrationID}`
                                                            }

                                                            className={
                                                                "DropDownField form-control"
                                                            }

                                                            onChange={e =>
                                                                handleWaiverChange(
                                                                    registrationID,
                                                                    e.target.value
                                                                )
                                                            }

                                                            value={
                                                                waiverAgreed
                                                                    ? "1"
                                                                    : "0"
                                                            }

                                                        >

                                                            <option value="0">
                                                                I have not yet agreed to the waiver
                                                            </option>

                                                            <option value="1">
                                                                Yes, I agree
                                                                to the waiver
                                                            </option>

                                                        </select>


                                                        <small>

                                                            {
                                                                agreementTemplate?.instructions
                                                            }{" "}

                                                            <Link
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                to={
                                                                    agreementTemplate?.pdfFileUrl
                                                                }
                                                            >
                                                                {
                                                                    agreementTemplate?.linkText
                                                                }
                                                            </Link>

                                                        </small>


                                                        {waiverMissing && (

                                                            <div
                                                                className={
                                                                    styles.capacityError
                                                                }
                                                            >
                                                                You must agree
                                                                to the waiver
                                                                to include this
                                                                registration.
                                                            </div>

                                                        )}

                                                    </div>

                                                </div>

                                            )
                                        }

                                    </div>

                                </div>

                            </div>

                        );

                    }
                )}

            </div>


            {/* ============================================= */}
            {/* CONTINUE */}
            {/* ============================================= */}

            <div
                className={
                    projStyles.actionsBelowPanel
                }
            >

                <button
    type="button"
    className={`
        button-default
        ${projStyles.continueBtn}
    `}
    onClick={handleRegister}
    disabled={!anySelected || !allSelectedReady}
    title={
        !anySelected
            ? "Select at least one registration"
            : !allSelectedReady
                ? "Please agree to all required waivers to continue"
                : ""
    }
>
    Continue
</button>


                {(!anySelected || !allSelectedReady) && (
    <small className={styles.campFooterHint}>
        {!anySelected
            ? "Select a quantity above to continue."
            : "Please agree to all required waivers to continue."}
    </small>
)}

            </div>

        </>

    );

}


export default EventRegistration;